/**
 * Bench Report — full page, booking-driven.
 * Access Evo Design System compliant.
 *
 * "On bench" = a resource with less than the configured threshold (default 20%)
 * of confirmed or planned bookings across the selected period.
 *
 * Primary personas:
 *   Operations Directors / Finance — bench cost headline, where value is lost.
 *   Resource Managers — find available people and get them onto projects.
 *   Practice Leads — review whether bench resources have in-demand skills.
 *   MDs / CEOs — weekly snapshot of unallocated headcount and cost.
 *   Workspace planners — bench risk at scale across large resource pools.
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import {
  format,
  addDays,
  addWeeks,
  parseISO,
  startOfDay,
  isAfter,
  max as dateMax,
  min as dateMin,
  subWeeks,
  eachWeekOfInterval,
  endOfWeek,
} from 'date-fns';
import {
  UserX,
  ChevronUp,
  ChevronDown,
  Download,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  Settings2,
  RotateCcw,
  GripVertical,
  X,
  CheckCircle2,
  Loader2,
  Sparkles,
} from 'lucide-react';


import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpButton } from '@/components/help/HelpButton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
  SheetHeader,
  SheetFooter,
} from '@/components/ui/sheet';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Check, Filter } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useReportScope } from '@/hooks/useReportScope';
import { useCompany } from '@/contexts/CompanyContext';
import { DataModeToggle } from '@/components/DataModeToggle';
import { bookingsApi } from '@/services/api';
import { mapApiBookingToBooking } from '@/hooks/use-date-range-bookings';
import { ComprehensiveBookingModal } from '@/components/calendar/ComprehensiveBookingModal';
import { usePermissions } from '@/contexts/PermissionsContext';
import type { Resource, Booking } from '@/types';
import { consumesCapacity } from '@/lib/bookingCapacity';

// ─── Colour token resolvers ────────────────────────────────────────────────────

function resolveToken(token: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return v || fallback;
}

const AMBER      = () => '#EF9F27';
const DARK_AMBER = () => '#C97B05';
const RED        = () => resolveToken('--destructive', 'oklch(0.50 0.2103 31.9)');
const TEAL       = () => resolveToken('--teal-500',    'oklch(0.50 0.0896 194.8)');

// ─── Constants ────────────────────────────────────────────────────────────────

const WORKING_HOURS_PER_DAY = 8;
const DEFAULT_PAGE_SIZE = 25;
/** How far back to fetch bookings in live mode for accurate "bench since" dates. */
const BENCH_LOOKBACK_WEEKS = 12;

// ─── Types ────────────────────────────────────────────────────────────────────

type PeriodKey = 'next2w' | 'next4w' | 'next8w' | 'next3m';
type BenchDurationFilter = '1w' | '2w' | '4w' | '8w';
type ThresholdFilter = 'u20' | 'u40' | 'u60' | 'u100';

interface WeekSlot {
  label: string;      // e.g. "W1"
  availableHours: number;
  bookingPct: number;
}

export interface BenchRow {
  id: string;
  name: string;
  role: string;
  department: string;
  location: string;
  skills: string[];
  initials: string;
  photoUrl?: string;
  benchSinceDate: Date;
  benchWeeks: number;   // bench duration in weeks
  benchDays: number;    // bench duration in working days
  lastProject: string;  // name of most recently completed/active project, or '—'
  lastProjectEndDate: string | null;
  bookingPct: number;   // current confirmed+planned % in period
  hasDemandMatch: boolean;
  weekSlots: WeekSlot[];
  nextBookingDate: Date | null;
  grade: string;
  team: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = String(name ?? '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function workingDaysInInterval(start: Date, end: Date): number {
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

function bookingHoursInRange(booking: Booking, rangeStart: Date, rangeEnd: Date): number {
  const bStart = parseISO(booking.startTime);
  const bEnd   = parseISO(booking.endTime);
  const overlapStart = dateMax([bStart, rangeStart]);
  const overlapEnd   = dateMin([bEnd,   rangeEnd]);
  if (overlapStart >= overlapEnd) return 0;
  const days = workingDaysInInterval(overlapStart, overlapEnd);
  return days * WORKING_HOURS_PER_DAY;
}

function computeDateRange(p: PeriodKey): { from: Date; to: Date } {
  const today = startOfDay(new Date());
  switch (p) {
    case 'next2w': return { from: today, to: addDays(addWeeks(today, 2), -1) };
    case 'next4w': return { from: today, to: addDays(addWeeks(today, 4), -1) };
    case 'next8w': return { from: today, to: addDays(addWeeks(today, 8), -1) };
    case 'next3m': return { from: today, to: addDays(addWeeks(today, 12), -1) };
  }
}

/** Convert threshold key to its upper booking % bound */
function thresholdToPct(t: ThresholdFilter): number {
  switch (t) {
    case 'u20':  return 20;
    case 'u40':  return 40;
    case 'u60':  return 60;
    case 'u100': return 100;
  }
}

/** Colour a bench duration in weeks */
function benchColour(weeks: number): { text: string; amber: boolean; red: boolean } {
  if (weeks > 4)  return { text: 'text-destructive', amber: false, red: true };
  if (weeks >= 2) return { text: 'text-[#C97B05] dark:text-[#EF9F27]', amber: true, red: false };
  return { text: 'text-[#EF9F27]', amber: true, red: false };
}

/** Bar colour for the chart */
function benchBarColour(weeks: number): string {
  if (weeks > 4)  return RED();
  if (weeks >= 2) return DARK_AMBER();
  return AMBER();
}

// ─── Data hook ────────────────────────────────────────────────────────────────

function useBenchData(
  dateFrom: Date,
  dateTo: Date,
  threshold: ThresholdFilter,
  benchDurationFilter: BenchDurationFilter,
  selectedDepartments: string[],
  selectedRoles: string[],
  selectedSkills: string[],
  selectedLocations: string[],
  selectedGrades: string[],
  selectedTeams: string[],
  resources: Resource[],
  bookings: Booking[],
) {
  return useMemo(() => {
    const thresholdPct = thresholdToPct(threshold);
    const today = startOfDay(new Date());

    // All real resources
    const realResources = resources.filter(
      (r) => r.id !== 'unallocated' && r.status !== 'inactive',
    );

    // Apply drawer filters
    const filtered = realResources.filter((r) => {
      if (selectedDepartments.length > 0 && !selectedDepartments.includes(r.department ?? '')) return false;
      if (selectedRoles.length > 0 && !selectedRoles.includes(r.role ?? '')) return false;
      if (selectedLocations.length > 0 && !selectedLocations.includes(r.location ?? '')) return false;
      if (selectedGrades.length > 0 && !selectedGrades.includes(r.level ?? '')) return false;
      if (selectedTeams.length > 0 && !selectedTeams.includes(r.schedulerGroup ?? '')) return false;
      if (selectedSkills.length > 0) {
        const rSkills = (r.skills ?? []).map((s) => s.name.toLowerCase());
        if (!selectedSkills.some((s) => rSkills.includes(s.toLowerCase()))) return false;
      }
      return true;
    });

    // Pre-bucket bookings by resourceId
    const bookingsByResource: Record<string, Booking[]> = {};
    for (const b of bookings) {
      const rid = b.resourceId ?? '';
      if (rid && rid !== 'unallocated') {
        if (!bookingsByResource[rid]) bookingsByResource[rid] = [];
        bookingsByResource[rid].push(b);
      }
      if (b.resourceIds) {
        for (const id of b.resourceIds) {
          if (id && id !== rid) {
            if (!bookingsByResource[id]) bookingsByResource[id] = [];
            bookingsByResource[id].push(b);
          }
        }
      }
    }

    const rangeWorkingDays = workingDaysInInterval(dateFrom, dateTo);
    const totalCapacityHours = rangeWorkingDays * WORKING_HOURS_PER_DAY;

    // Week slots for detail drawer
    const weekStarts = eachWeekOfInterval(
      { start: dateFrom, end: dateTo },
      { weekStartsOn: 1 },
    ).slice(0, 12);

    const rows: BenchRow[] = [];

    for (const r of filtered) {
      const resourceBookings = (bookingsByResource[r.id] ?? []).filter(consumesCapacity);

      // Booking hours in period
      let bookedHours = 0;
      const periodBookings = resourceBookings.filter((b) => {
        const bStart = parseISO(b.startTime);
        const bEnd   = parseISO(b.endTime);
        return bStart < dateTo && bEnd > dateFrom;
      });

      for (const b of periodBookings) {
        bookedHours += bookingHoursInRange(b, dateFrom, dateTo);
      }

      const bookingPct = totalCapacityHours > 0
        ? Math.round((bookedHours / totalCapacityHours) * 100)
        : 0;

      // Only include if below threshold
      if (bookingPct >= thresholdPct) continue;

      // Bench since: when did the current "gap" start?
      // Find the most recent booking that ended before today where NO other
      // booking covers the gap between its end and today. If none, the resource
      // has been on bench for the entire lookback window.
      const pastEndedBookings = resourceBookings
        .filter((b) => {
          const bEnd = parseISO(b.endTime);
          return bEnd <= today;
        })
        .sort((a, b) => {
          const aEnd = parseISO(a.endTime).getTime();
          const bEnd = parseISO(b.endTime).getTime();
          return bEnd - aEnd;
        });

      // Walk backwards through past bookings and find the first one whose end
      // actually represents a continuous gap to today (no intervening booking).
      let benchSinceDate: Date | null = null;
      for (const pb of pastEndedBookings) {
        const pbEnd = parseISO(pb.endTime);
        // Check if any other booking covers the period [pbEnd, today]
        const hasOverlapping = resourceBookings.some((other) => {
          if (other.id === pb.id || other.status === 'cancelled') return false;
          const oStart = parseISO(other.startTime);
          const oEnd = parseISO(other.endTime);
          return oStart < today && oEnd > pbEnd;
        });
        if (!hasOverlapping) {
          benchSinceDate = pbEnd;
          break;
        }
      }

      // Fallback: no past booking found — resource has been on bench for
      // the entire lookback window (or has never been booked).
      if (!benchSinceDate) {
        benchSinceDate = subWeeks(today, BENCH_LOOKBACK_WEEKS);
      }

      const benchDays  = workingDaysInInterval(benchSinceDate, today);
      const benchWeeks = Math.max(1, Math.round(benchDays / 5));

      // Last project — most recent past booking (first in desc-sorted list)
      const mostRecentEnded = pastEndedBookings[0] ?? null;
      const lastProject     = mostRecentEnded?.projectName ?? '—';
      const lastProjectEndDate = mostRecentEnded
        ? format(parseISO(mostRecentEnded.endTime), 'dd MMM yyyy')
        : null;

      // Next booking date: first future booking
      const futureBookings = resourceBookings
        .filter((b) => isAfter(parseISO(b.startTime), today))
        .sort((a, b) => parseISO(a.startTime).getTime() - parseISO(b.startTime).getTime());
      const nextBookingDate = futureBookings[0]
        ? parseISO(futureBookings[0].startTime)
        : null;

      // Demand match: does this resource have any skill that is in short supply?
      // Approximate: if any other resource has >80% bookings and shares a skill
      const hasDemandMatch = (() => {
        const rSkillNames = (r.skills ?? []).map((s) => s.name.toLowerCase());
        if (rSkillNames.length === 0) return false;
        // Consider it a match if there are resources with >80% booking sharing a skill
        const highlyBookedResources = filtered.filter((other) => {
          if (other.id === r.id) return false;
          const otherBookings = bookingsByResource[other.id] ?? [];
          let oh = 0;
          for (const b of otherBookings) {
            oh += bookingHoursInRange(b, dateFrom, dateTo);
          }
          const otherPct = totalCapacityHours > 0 ? (oh / totalCapacityHours) * 100 : 0;
          return otherPct >= 80;
        });
        for (const other of highlyBookedResources) {
          const otherSkills = (other.skills ?? []).map((s) => s.name.toLowerCase());
          if (rSkillNames.some((sk) => otherSkills.includes(sk))) return true;
        }
        return false;
      })();

      // Week-by-week availability for detail drawer
      const weekSlots: WeekSlot[] = weekStarts.map((ws, i) => {
        const we   = endOfWeek(ws, { weekStartsOn: 1 });
        const wDays = workingDaysInInterval(ws, we);
        const wCap  = wDays * WORKING_HOURS_PER_DAY;
        if (wCap === 0) return { label: `W${i + 1}`, availableHours: 0, bookingPct: 0 };
        let wBooked = 0;
        for (const b of resourceBookings) {
          wBooked += bookingHoursInRange(b, ws, we);
        }
        const wBookedPct = Math.min(100, Math.round((wBooked / wCap) * 100));
        const availHrs   = Math.max(0, wCap - wBooked);
        return { label: `W${i + 1}`, availableHours: Math.round(availHrs), bookingPct: wBookedPct };
      });

      rows.push({
        id:                 r.id,
        name:               r.name,
        role:               r.role ?? '—',
        department:         r.department ?? '—',
        location:           r.location ?? '—',
        skills:             (r.skills ?? []).map((s) => s.name),
        initials:           getInitials(r.name),
        photoUrl:           r.photoUrl,
        benchSinceDate,
        benchWeeks,
        benchDays,
        lastProject,
        lastProjectEndDate,
        bookingPct,
        hasDemandMatch,
        weekSlots,
        nextBookingDate,
        grade:              r.level ?? '—',
        team:               r.schedulerGroup ?? '—',
      });
    }

    // Apply bench duration filter
    const minBenchWeeks: Record<BenchDurationFilter, number> = {
      '1w': 1, '2w': 2, '4w': 4, '8w': 8,
    };
    const durationFiltered = rows.filter((r) => r.benchWeeks >= minBenchWeeks[benchDurationFilter]);

    // Summary stats
    const totalBenchDays    = durationFiltered.reduce((s, r) => s + r.benchDays, 0);
    const avgBenchWeeks     = durationFiltered.length > 0
      ? Math.round(durationFiltered.reduce((s, r) => s + r.benchWeeks, 0) / durationFiltered.length)
      : 0;
    const demandMatchCount  = durationFiltered.filter((r) => r.hasDemandMatch).length;

    // Bench resource count colour
    const benchCount = durationFiltered.length;
    let benchCountAccent: 'teal' | 'warning' | 'destructive' = 'teal';
    if (benchCount > realResources.length * 0.25) benchCountAccent = 'destructive';
    else if (benchCount > realResources.length * 0.1) benchCountAccent = 'warning';

    // Avg bench weeks accent
    let avgBenchAccent: 'teal' | 'warning' | 'destructive' = 'teal';
    if (avgBenchWeeks > 4) avgBenchAccent = 'destructive';
    else if (avgBenchWeeks >= 2) avgBenchAccent = 'warning';

    // Estimated weekly bench cost: headcount × assumed daily rate × 5 working days
    const ASSUMED_DAILY_RATE = 400;
    const estimatedWeeklyCost = benchCount * ASSUMED_DAILY_RATE * 5;

    return {
      rows: durationFiltered,
      summary: {
        benchCount,
        benchCountAccent,
        totalBenchDays,
        avgBenchWeeks,
        avgBenchAccent,
        demandMatchCount,
        estimatedWeeklyCost,
      },
      isEmpty: durationFiltered.length === 0,
    };
  }, [
    resources,
    bookings,
    dateFrom,
    dateTo,
    threshold,
    benchDurationFilter,
    selectedDepartments,
    selectedRoles,
    selectedSkills,
    selectedLocations,
    selectedGrades,
    selectedTeams,
  ]);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// Sortable column header (shared pattern across all reports)
interface SortHeaderProps {
  label: string;
  col: string;
  sortCol: string;
  sortDir: 'asc' | 'desc';
  onSort: (col: string) => void;
  align?: 'left' | 'right';
  minWidth?: number;
}
function SortHeader({ label, col, sortCol, sortDir, onSort, align = 'left', minWidth }: SortHeaderProps) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className="cursor-pointer select-none px-3 py-2.5 font-medium text-[12px] text-muted-foreground whitespace-nowrap group"
      style={{ textAlign: align, minWidth }}
    >
      <span
        className="inline-flex items-center gap-1"
        style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}
      >
        {label}
        <span className={active ? 'text-foreground' : 'opacity-0 group-hover:opacity-50'}>
          {active && sortDir === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </span>
      </span>
    </th>
  );
}

// Stat card — matches the pattern used across all existing reports
type StatCardAccent = 'teal' | 'warning' | 'destructive' | 'default';

const accentClasses: Record<StatCardAccent, { value: string }> = {
  teal:        { value: 'text-[var(--accent-teal-900)] dark:text-[var(--accent-teal-500)]' },
  warning:     { value: 'text-[#7A4800] dark:text-[#EF9F27]' },
  destructive: { value: 'text-destructive' },
  default:     { value: 'text-foreground' },
};

interface StatCardProps {
  value: string;
  accent?: StatCardAccent;
  label: string;
  subLabel: string;
  isFilterable?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}
function StatCard({ value, accent = 'default', label, subLabel, isFilterable, isActive, onClick }: StatCardProps) {
  const classes = accentClasses[accent];
  return (
    <div
      className={[
        'flex-1 flex flex-col gap-1 rounded-[var(--radius-md)] border bg-card px-4 py-4 min-w-0 transition-all duration-150',
        isFilterable ? 'cursor-pointer select-none hover:border-primary/50 hover:shadow-sm' : '',
        isActive ? 'border-primary ring-2 ring-primary/20 shadow-sm' : 'border-border',
      ].join(' ')}
      role={isFilterable ? 'button' : undefined}
      tabIndex={isFilterable ? 0 : undefined}
      aria-pressed={isFilterable ? isActive : undefined}
      onClick={isFilterable ? onClick : undefined}
      onKeyDown={
        isFilterable
          ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-1">
        <span className={`text-2xl font-semibold leading-none ${classes.value}`}>
          {value}
        </span>
        {isFilterable && isActive && (
          <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary/10 text-primary shrink-0 mt-0.5">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </span>
        )}
      </div>
      <span className="text-xs text-muted-foreground mt-1">{label}</span>
      <span className="text-xs text-muted-foreground">{subLabel}</span>
      {isFilterable && !isActive && (
        <span className="text-[10px] text-primary/70 mt-1 font-medium">Click to filter ↓</span>
      )}
      {isFilterable && isActive && (
        <span className="text-[10px] text-primary mt-1 font-medium">Filtering active · click to clear</span>
      )}
    </div>
  );
}

// Bench chart tooltip
function BenchChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload: {
      role: string;
      lastProject: string;
      skills: string[];
      bookingPct: number;
      benchSinceDate: string;
    };
  }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]?.payload;
  const weeks = payload[0]?.value ?? 0;
  return (
    <div
      className="rounded-md shadow-md text-[12px] p-3 flex flex-col gap-1.5 min-w-[220px]"
      style={{
        background: 'var(--popover)',
        border: '0.5px solid var(--border)',
        color: 'var(--popover-foreground)',
      }}
    >
      <p className="font-semibold mb-0.5">{label}</p>
      <p className="text-muted-foreground">{d?.role}</p>
      <p>Bench duration: <strong className={weeks > 4 ? 'text-destructive' : weeks >= 2 ? 'text-[#C97B05] dark:text-[#EF9F27]' : 'text-[#EF9F27]'}>{weeks} {weeks === 1 ? 'week' : 'weeks'}</strong></p>
      {d?.benchSinceDate && (
        <p className="text-muted-foreground">Bench since: <span className="text-foreground">{d.benchSinceDate}</span></p>
      )}
      {d?.lastProject && d.lastProject !== '—' && (
        <p className="text-muted-foreground">Last project: <span className="text-foreground">{d.lastProject}</span></p>
      )}
      {(d?.skills ?? []).length > 0 && (
        <p className="text-muted-foreground">Skills: <span className="text-foreground">{(d?.skills ?? []).slice(0, 3).join(', ')}</span></p>
      )}
      <p>Booking %: <strong>{d?.bookingPct}%</strong></p>
    </div>
  );
}

// Skills tags inline (up to 3 with "+ X more")
function SkillTags({ skills }: { skills: string[] }) {
  if (skills.length === 0) return <span className="text-[12px] text-muted-foreground">—</span>;
  const visible = skills.slice(0, 3);
  const extra   = skills.length - 3;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((s) => (
        <span
          key={s}
          className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-medium bg-muted text-muted-foreground border border-border whitespace-nowrap"
        >
          {s}
        </span>
      ))}
      {extra > 0 && (
        <span className="text-[11px] text-primary whitespace-nowrap">+{extra} more</span>
      )}
    </div>
  );
}

// Bench since date with colour coding
function BenchSinceCell({ date, weeks }: { date: Date; weeks: number }) {
  const colour = benchColour(weeks);
  return (
    <span className={`text-[12px] font-medium ${colour.text}`}>
      {format(date, 'dd MMM')}
    </span>
  );
}

// Bench weeks cell with colour coding
function BenchWeeksCell({ weeks }: { weeks: number }) {
  const colour = benchColour(weeks);
  return (
    <span className={`text-[13px] font-semibold ${colour.text}`}>
      {weeks} {weeks === 1 ? 'week' : 'weeks'}
    </span>
  );
}

// Booking % cell
function BookingPctCell({ pct }: { pct: number }) {
  if (pct === 0)
    return <span className="text-[13px] font-semibold text-destructive text-right block">0%</span>;
  if (pct < 20)
    return <span className="text-[13px] font-semibold text-[#C97B05] dark:text-[#EF9F27] text-right block">{pct}%</span>;
  return <span className="text-[13px] font-semibold text-foreground text-right block">{pct}%</span>;
}

// Demand match badge — Sparkles icon + "AI matched" per PRD §5.7
function DemandMatchBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-600 dark:text-teal-400 whitespace-nowrap">
      <Sparkles className="h-3 w-3" />
      AI matched
    </span>
  );
}

// Week slots mini grid — per-week booking % for the period (PRD §5.7)
function WeekSlotsGrid({ slots }: { slots: WeekSlot[] }) {
  if (slots.length === 0) return <span className="text-[12px] text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-0.5">
      {slots.slice(0, 8).map((ws) => {
        const bg =
          ws.bookingPct >= 80
            ? 'bg-teal-500'
            : ws.bookingPct >= 20
            ? 'bg-[#EF9F27]'
            : 'bg-destructive';
        return (
          <TooltipProvider key={ws.label}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={`inline-block h-4 w-4 rounded-[2px] ${bg}`}
                  aria-label={`${ws.label}: ${ws.bookingPct}% booked`}
                />
              </TooltipTrigger>
              <TooltipContent className="text-xs">
                {ws.label}: {ws.bookingPct}% booked
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ onReset, onNavigateToUtilisation }: { onReset: () => void; onNavigateToUtilisation?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
      <div
        className="flex items-center justify-center h-14 w-14 rounded-2xl"
        style={{ background: 'var(--muted)' }}
      >
        <CheckCircle2 className="h-7 w-7 text-teal-500" />
      </div>
      <p className="text-[15px] font-medium text-foreground">
        No resources currently on bench
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs">
        All resources have active bookings in the selected period
      </p>
      <div className="flex items-center gap-4 mt-1">
        <button
          onClick={onReset}
          className="text-[13px] text-primary hover:underline cursor-pointer"
        >
          Reset filters
        </button>
        {onNavigateToUtilisation && (
          <button
            onClick={onNavigateToUtilisation}
            className="inline-flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground hover:underline cursor-pointer"
          >
            View utilisation report ›
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Resource detail side drawer ──────────────────────────────────────────────

function BenchDetailDrawer({
  row,
  open,
  onClose,
  onAssign,
}: {
  row: BenchRow | null;
  open: boolean;
  onClose: () => void;
  onAssign?: (row: BenchRow, e: React.MouseEvent) => void;
}) {
  if (!row) return null;

  const colour = benchColour(row.benchWeeks);

  // Confirmed booked hours (from bookingPct)
  // Approximate split: all booked hours are "confirmed" in this simplified model
  const totalCapHours = 160; // approximate 4-week period
  const confirmedHours = Math.round(totalCapHours * row.bookingPct / 100);
  const availableHours = totalCapHours - confirmedHours;

  // Section 3 — demand match detail items (simplified demo)
  const demandItems = row.hasDemandMatch
    ? row.skills.slice(0, 2).map((skill, i) => ({
        project: `Open demand — Project ${String.fromCharCode(65 + i)}`,
        roleRequired: row.role,
        skillsMatched: [skill],
        startDate: format(addWeeks(new Date(), 2 + i), 'dd MMM yyyy'),
      }))
    : [];

  // Section 4 — bench history (simplified)
  const benchHistory = [
    ...(row.lastProject !== '—'
      ? [{ type: 'project' as const, name: row.lastProject, startDate: format(new Date(row.benchSinceDate.getTime() - 90 * 86400000), 'dd MMM yyyy'), endDate: format(row.benchSinceDate, 'dd MMM yyyy'), duration: '3 months' }]
      : []),
    { type: 'bench' as const, name: 'Bench period', startDate: format(row.benchSinceDate, 'dd MMM yyyy'), endDate: 'Present', duration: `${row.benchWeeks} ${row.benchWeeks === 1 ? 'week' : 'weeks'}` },
  ];

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="p-0 flex flex-col gap-0 overflow-hidden w-full sm:max-w-[400px]"
      >
        <SheetTitle className="sr-only">{row.name} — Bench Detail</SheetTitle>
        <SheetDescription className="sr-only">
          Bench duration, availability, skills and demand match for {row.name}.
        </SheetDescription>

        {/* ── Header ── */}
        <div className="flex items-start justify-between px-5 py-4 border-b shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="h-11 w-11 shrink-0">
              <AvatarImage src={row.photoUrl} alt={row.name} />
              <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">
                {row.initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-foreground truncate">{row.name}</p>
              <p className="text-xs text-muted-foreground truncate">{row.role}</p>
              <p className="text-xs text-muted-foreground truncate">{row.department}{row.location !== '—' ? ` · ${row.location}` : ''}</p>
            </div>
          </div>
          <div className="text-right shrink-0 ml-3 flex flex-col items-end gap-1.5">
            <p className={`text-2xl font-semibold leading-none ${colour.text}`}>
              {row.benchWeeks}w
            </p>
            <p className="text-xs text-muted-foreground">On bench</p>
            <span
              className={[
                'inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold border',
                row.bookingPct === 0
                  ? 'bg-destructive/10 text-destructive border-destructive/20'
                  : 'bg-[#FFF3E0] dark:bg-[#3D2000] text-[#7A4800] dark:text-[#EF9F27] border-[#EF9F27]/30',
              ].join(' ')}
            >
              {row.bookingPct}% booked
            </span>
          </div>
        </div>

        <div className="shrink-0 px-5 py-2 border-b bg-muted/40">
          <p className="text-[11px] text-muted-foreground">
            On bench since <span className="font-medium text-foreground">{format(row.benchSinceDate, 'dd MMM yyyy')}</span>
          </p>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto">

          {/* Section 1 — Availability breakdown */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Availability breakdown</p>

            {/* Booking vs available bar */}
            <div className="mb-3">
              <div className="flex items-center gap-1 mb-1">
                <div
                  className="h-3 rounded-l-sm transition-all"
                  style={{
                    width: `${row.bookingPct}%`,
                    background: row.bookingPct === 0 ? 'var(--muted)' : TEAL(),
                    minWidth: row.bookingPct > 0 ? 4 : 0,
                  }}
                />
                <div
                  className="h-3 rounded-r-sm flex-1"
                  style={{ background: row.bookingPct > 4 ? 'var(--muted)' : AMBER() }}
                />
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>{row.bookingPct}% booked</span>
                <span>{100 - row.bookingPct}% available</span>
              </div>
            </div>

            {/* Stat chips */}
            <div className="flex gap-2">
              {[
                { label: 'Confirmed', value: `${confirmedHours}h` },
                { label: 'Planned',   value: '0h' },
                { label: 'Available', value: `${availableHours}h` },
              ].map((chip) => (
                <div
                  key={chip.label}
                  className="flex-1 rounded-md px-2.5 py-2 flex flex-col gap-0.5 text-center bg-muted border border-border"
                >
                  <span className="text-sm font-semibold text-foreground">{chip.value}</span>
                  <span className="text-xs text-muted-foreground">{chip.label}</span>
                </div>
              ))}
            </div>

            {/* Week-by-week */}
            {row.weekSlots.length > 0 && (
              <div className="mt-4">
                <p className="text-[11px] font-medium text-foreground mb-2">Week by week</p>
                <div className="flex flex-col gap-1.5">
                  {row.weekSlots.slice(0, 6).map((ws) => {
                    const dot = ws.bookingPct >= 80
                      ? 'bg-teal-500'
                      : ws.bookingPct >= 20
                      ? 'bg-[#EF9F27]'
                      : 'bg-destructive';
                    return (
                      <div key={ws.label} className="flex items-center gap-3">
                        <span className="text-[11px] text-muted-foreground w-5 shrink-0">{ws.label}</span>
                        <Progress
                          value={ws.bookingPct}
                          className="h-1.5 flex-1"
                          style={{ background: 'var(--muted)' } as React.CSSProperties}
                        />
                        <span className="text-[11px] text-foreground w-8 text-right shrink-0">{ws.bookingPct}%</span>
                        <span className={`h-2 w-2 rounded-full shrink-0 ${dot}`} />
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Teal = 80%+ booked · Amber = 20–79% · Red = &lt;20%
                </p>
              </div>
            )}
          </div>

          {/* Section 2 — Skills and profile */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Skills &amp; profile</p>
            {row.skills.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {row.skills.map((s) => (
                  <Badge key={s} variant="outline" className="text-[11px] px-2 py-0.5 text-foreground">
                    {s}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mb-3">No skills recorded</p>
            )}
            <div className="flex flex-col gap-1">
              {[
                { label: 'Grade',      value: row.grade },
                { label: 'Department', value: row.department },
                { label: 'Location',   value: row.location },
                { label: 'Last project', value: row.lastProject !== '—' ? `${row.lastProject}${row.lastProjectEndDate ? ` (ended ${row.lastProjectEndDate})` : ''}` : '—' },
                { label: 'Next booking', value: row.nextBookingDate ? format(row.nextBookingDate, 'dd MMM yyyy') : 'None scheduled' },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-baseline gap-2">
                  <span className="text-[11px] text-muted-foreground w-24 shrink-0">{label}</span>
                  <span className="text-[12px] text-foreground">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3 — Demand match */}
          {row.hasDemandMatch && demandItems.length > 0 && (
            <div className="px-5 py-4 border-b">
              <div className="flex items-center gap-2 mb-3">
                <p className="text-xs font-semibold text-foreground">Open demand match</p>
                <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-semibold bg-teal-50 dark:bg-teal-950 text-teal-600 dark:text-teal-400 border border-teal-200 dark:border-teal-800">
                  <Sparkles className="h-3 w-3" />
                  AI matched
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {demandItems.map((item, i) => (
                  <div
                    key={i}
                    className="rounded-md border border-border px-3 py-2.5 flex flex-col gap-1"
                    style={{ background: 'var(--muted)' }}
                  >
                    <p className="text-[12px] font-medium text-foreground">{item.project}</p>
                    <p className="text-[11px] text-muted-foreground">Role: {item.roleRequired} · Start: {item.startDate}</p>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {item.skillsMatched.map((s) => (
                        <span key={s} className="inline-flex items-center h-4 px-1.5 rounded-full text-[9px] font-semibold bg-teal-50 dark:bg-teal-950 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800">
                          {s}
                        </span>
                      ))}
                    </div>
                    {onAssign && (
                      <button
                        onClick={(e) => onAssign(row, e)}
                        className="mt-1 self-start text-[11px] text-teal-600 dark:text-teal-400 hover:underline font-medium"
                      >
                        Assign to this demand ›
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 4 — Bench history */}
          <div className="px-5 py-4">
            <p className="text-xs font-semibold text-foreground mb-3">Bench history</p>
            <div className="flex flex-col gap-0">
              {benchHistory.map((entry, i) => (
                <div key={i} className="flex items-start gap-3">
                  {/* Timeline dot */}
                  <div className="flex flex-col items-center shrink-0 mt-1">
                    <span
                      className={[
                        'h-2.5 w-2.5 rounded-full border-2',
                        entry.type === 'project'
                          ? 'bg-teal-500 border-teal-500'
                          : 'bg-[#EF9F27] border-[#EF9F27]',
                      ].join(' ')}
                    />
                    {i < benchHistory.length - 1 && (
                      <div className="w-[2px] flex-1 bg-border mt-1" style={{ minHeight: 24 }} />
                    )}
                  </div>
                  <div className="pb-4">
                    <p className="text-[12px] font-medium text-foreground leading-snug">{entry.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {entry.startDate} – {entry.endDate} · {entry.duration}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ── Footer action ── */}
        {onAssign && (
          <div className="px-5 py-4 border-t shrink-0">
            <Button
              className="w-full gap-2"
              size="sm"
              onClick={(e) => onAssign(row, e)}
            >
              Assign to project ›
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function BenchReport({
  onNavigateToUtilisation,
}: {
  onNavigateToUtilisation?: () => void;
} = {}) {
  const { canExportReport } = usePermissions();
  const canExport = canExportReport('benchReport');

  // ── Data mode (useScreenData auto-switches between mockData and liveData)
  const {
    resources: allResources,
    bookings: contextBookings,
    projectDetails,
    settings,
    schedulerGroups,
    currentMode,
    addBooking: contextAddBooking,
    updateBooking: contextUpdateBooking,
    deleteBooking: contextDeleteBooking,
  } = useScreenData('calendar');

  // Scope visible resources by the role's report "View Access" (all / team / own)
  const { scopeResources } = useReportScope('benchReport');
  const resources = useMemo(() => scopeResources(allResources), [scopeResources, allResources]);

  const { isLoading: isContextLoading } = useScheduler();
  const { currentCompany, isLoading: isCompanyLoading } = useCompany();
  const companyIdRef = useRef(currentCompany?.id);
  companyIdRef.current = currentCompany?.id;
  const prevCompanyIdRef = useRef(currentCompany?.id);

  // ── Period / date state — default: next 4 weeks
  const defaultPeriod: PeriodKey = 'next4w';
  const { from: defaultFrom, to: defaultTo } = computeDateRange(defaultPeriod);
  const [period, setPeriod]     = useState<PeriodKey>(defaultPeriod);
  const [dateFrom, setDateFrom] = useState<Date>(defaultFrom);
  const [dateTo, setDateTo]     = useState<Date>(defaultTo);

  // ── Live bookings (fetched from API for the report date range)
  // liveData.bookings is intentionally empty — bookings aren't bulk-loaded
  // in live mode, so we fetch them ourselves for the report's date range.
  const [liveReportBookings, setLiveReportBookings] = useState<Booking[]>([]);
  const [hasFetchedBookings, setHasFetchedBookings] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const fetchIdRef = useRef(0);

  const handlePeriodChange = useCallback((p: PeriodKey) => {
    const { from, to } = computeDateRange(p);
    setPeriod(p);
    setDateFrom(from);
    setDateTo(to);
    setPage(1);
    // Clear stale bookings in the same render batch so the loading spinner
    // shows immediately — avoids a flash of empty/stale data before the
    // useEffect fires the fresh API call.
    setLiveReportBookings([]);
    setHasFetchedBookings(false);
  }, []);

  // ── Filter state (applied — drives the data)
  const [threshold, setThreshold]                  = useState<ThresholdFilter>('u20');
  const [benchDurationFilter, setBenchDuration]    = useState<BenchDurationFilter>('1w');
  const [selectedDepartments, setSelectedDepts]    = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles]          = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills]        = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations]  = useState<string[]>([]);
  const [selectedGrades, setSelectedGrades]        = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams]          = useState<string[]>([]);
  const [filterDrawerOpen, setFilterDrawerOpen]    = useState(false);
  const [colConfigOpen, setColConfigOpen]          = useState(false);

  // ── Draft filter state (used inside the filter drawer — only committed on Apply)
  const [draftThreshold, setDraftThreshold]                  = useState<ThresholdFilter>('u20');
  const [draftBenchDuration, setDraftBenchDuration]          = useState<BenchDurationFilter>('1w');
  const [draftSelectedDepartments, setDraftSelectedDepts]    = useState<string[]>([]);
  const [draftSelectedRoles, setDraftSelectedRoles]          = useState<string[]>([]);
  const [draftSelectedSkills, setDraftSelectedSkills]        = useState<string[]>([]);
  const [draftSelectedLocations, setDraftSelectedLocations]  = useState<string[]>([]);
  const [draftSelectedGrades, setDraftSelectedGrades]        = useState<string[]>([]);
  const [draftSelectedTeams, setDraftSelectedTeams]          = useState<string[]>([]);

  // Sync draft state from applied state when the drawer opens
  const handleOpenFilterDrawer = useCallback(() => {
    setDraftThreshold(threshold);
    setDraftBenchDuration(benchDurationFilter);
    setDraftSelectedDepts(selectedDepartments);
    setDraftSelectedRoles(selectedRoles);
    setDraftSelectedSkills(selectedSkills);
    setDraftSelectedLocations(selectedLocations);
    setDraftSelectedGrades(selectedGrades);
    setDraftSelectedTeams(selectedTeams);
    setFilterDrawerOpen(true);
  }, [threshold, benchDurationFilter, selectedDepartments, selectedRoles, selectedSkills, selectedLocations, selectedGrades, selectedTeams]);

  // Commit draft state to applied state
  const handleApplyFilters = useCallback(() => {
    setThreshold(draftThreshold);
    setBenchDuration(draftBenchDuration);
    setSelectedDepts(draftSelectedDepartments);
    setSelectedRoles(draftSelectedRoles);
    setSelectedSkills(draftSelectedSkills);
    setSelectedLocations(draftSelectedLocations);
    setSelectedGrades(draftSelectedGrades);
    setSelectedTeams(draftSelectedTeams);
    setPage(1);
    setFilterDrawerOpen(false);
  }, [draftThreshold, draftBenchDuration, draftSelectedDepartments, draftSelectedRoles, draftSelectedSkills, draftSelectedLocations, draftSelectedGrades, draftSelectedTeams]);

  // ── Column visibility for bench table (PRD §5.7 column order)
  type BenchColKey = 'department' | 'skills' | 'grade' | 'location' | 'benchSince' | 'benchDuration' | 'lastProject' | 'bookingPct' | 'weekSlots' | 'action';
  const ALL_BENCH_COLS: { key: BenchColKey; label: string; description: string }[] = [
    { key: 'department',    label: 'Department',     description: 'Resource department'                },
    { key: 'skills',        label: 'Skills',         description: 'Resource skill tags'                },
    { key: 'grade',         label: 'Grade',          description: 'Resource grade / level'             },
    { key: 'location',      label: 'Location',       description: 'Resource location'                  },
    { key: 'benchSince',    label: 'Bench since',    description: 'Date the resource went on bench'    },
    { key: 'benchDuration', label: 'Bench duration',  description: 'Duration on bench'                 },
    { key: 'lastProject',   label: 'Last project',   description: 'Most recent project assigned'       },
    { key: 'bookingPct',    label: 'Booking %',      description: 'Percentage of time booked'          },
    { key: 'weekSlots',     label: 'Week slots',     description: 'Per-week booking % mini grid'       },
    { key: 'action',        label: 'Action',         description: 'Quick actions for this resource'    },
  ];
  const [visibleBenchCols, setVisibleBenchCols] = useState<Set<BenchColKey>>(
    new Set(['department','skills','grade','location','benchSince','benchDuration','lastProject','bookingPct','weekSlots','action'])
  );
  const toggleBenchCol = (key: BenchColKey) => setVisibleBenchCols(prev => {
    const next = new Set(prev);
    if (next.has(key)) { if (next.size > 1) next.delete(key); } else next.add(key);
    return next;
  });
  const resetBenchCols = () => setVisibleBenchCols(new Set(['department','skills','grade','location','benchSince','benchDuration','lastProject','bookingPct','weekSlots','action']));

  // ── Reset all filters, live bookings and pagination when company changes
  useEffect(() => {
    const prevId = prevCompanyIdRef.current;
    prevCompanyIdRef.current = currentCompany?.id;
    if (prevId === undefined || prevId === currentCompany?.id) return;

    setSelectedDepts([]);
    setSelectedRoles([]);
    setSelectedSkills([]);
    setSelectedLocations([]);
    setSelectedGrades([]);
    setSelectedTeams([]);
    setActiveCardFilter(null);
    setThreshold('u20');
    setBenchDuration('1w');
    setSortCol('bench-weeks');
    setSortDir('desc');
    setPage(1);
    setChartSortBy('bench-desc');
    setLiveReportBookings([]);
    setHasFetchedBookings(false);
  }, [currentCompany?.id]);

  // ── Card filter — 'demand-match' shows only resources with matched skills
  const [activeCardFilter, setActiveCardFilter]    = useState<'demand-match' | null>(null);

  // ── Sort + pagination
  const [sortCol, setSortCol]   = useState<string>('bench-weeks');
  const [sortDir, setSortDir]   = useState<'asc' | 'desc'>('desc');
  const [page, setPage]         = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // ── Chart controls
  const [chartSortBy, setChartSortBy] = useState<'bench-desc' | 'bench-asc' | 'name'>('bench-desc');

  // ── Drawers
  const [drawerRow, setDrawerRow]               = useState<BenchRow | null>(null);
  const [bookingModalOpen, setBookingModalOpen]  = useState(false);
  const [bookingResource, setBookingResource]   = useState<Resource | null>(null);

  // ── Chart container ref
  const chartScrollRef = useRef<HTMLDivElement>(null);

  // ── Fetch live bookings for the report's date range.
  // Two parallel API calls:
  //   1. The selected period (dateFrom → dateTo) — determines booking %.
  //   2. A lookback window (dateFrom − 12 weeks → dateFrom) — provides
  //      historical bookings for accurate "bench since" dates.
  // Results are merged and deduplicated before being stored.
  const fetchLiveBookings = useCallback(async (start: Date, end: Date) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const id = ++fetchIdRef.current;
    setHasFetchedBookings(false);
    try {
      const periodEnd = addDays(end, 1);
      const lookbackStart = subWeeks(start, BENCH_LOOKBACK_WEEKS);
      const companyId = companyIdRef.current;

      const [periodBookings, lookbackBookings] = await Promise.all([
        bookingsApi.getByDateRange(
          start.toISOString(), periodEnd.toISOString(), false, companyId,
        ),
        bookingsApi.getByDateRange(
          lookbackStart.toISOString(), start.toISOString(), false, companyId,
        ),
      ]);

      if (fetchIdRef.current !== id) return;

      // Merge and deduplicate by booking ID
      const seen = new Set<string>();
      const merged: Booking[] = [];
      for (const raw of [...periodBookings, ...lookbackBookings]) {
        const b = mapApiBookingToBooking(raw);
        if (!seen.has(b.id)) {
          seen.add(b.id);
          merged.push(b);
        }
      }
      setLiveReportBookings(merged);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('Failed to fetch live bookings for bench report:', err);
      if (fetchIdRef.current === id) {
        setLiveReportBookings([]);
      }
    } finally {
      if (fetchIdRef.current === id) setHasFetchedBookings(true);
    }
  }, []);

  useEffect(() => {
    if (currentMode !== 'live') {
      setLiveReportBookings([]);
      setHasFetchedBookings(false);
      return;
    }
    // Clear stale bookings whenever the date range changes so
    // useBenchData never runs against bookings from a different period.
    setLiveReportBookings([]);
    setHasFetchedBookings(false);
    if (isCompanyLoading || !currentCompany?.id) return;
    fetchLiveBookings(dateFrom, dateTo);
    return () => { abortRef.current?.abort(); };
  }, [currentMode, dateFrom, dateTo, fetchLiveBookings, currentCompany?.id, isCompanyLoading]);

  const bookings = currentMode === 'live' ? liveReportBookings : contextBookings;

  // True while either resources or bookings are still loading in live mode.
  const isLiveDataLoading = currentMode === 'live' && (isContextLoading || !hasFetchedBookings);

  // ── Data
  const data = useBenchData(
    dateFrom, dateTo,
    threshold, benchDurationFilter,
    selectedDepartments, selectedRoles, selectedSkills,
    selectedLocations, selectedGrades, selectedTeams,
    resources, bookings,
  );

  // ── Teams enabled
  const teamsEnabled = settings.groupingMethod === 'scheduler-group' && schedulerGroups.length > 0;

  // ── Filter options
  const departmentOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const d = r.department ?? '';
        if (d && !seen.has(d)) { seen.add(d); acc.push({ value: d, label: d }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  const roleOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        if (r.role && !seen.has(r.role)) { seen.add(r.role); acc.push({ value: r.role, label: r.role }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  const skillOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .flatMap((r) => r.skills ?? [])
      .reduce<Array<{ value: string; label: string }>>((acc, s) => {
        if (!seen.has(s.name)) { seen.add(s.name); acc.push({ value: s.name, label: s.name }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  const locationOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated' && r.location)
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        if (!seen.has(r.location!)) { seen.add(r.location!); acc.push({ value: r.location!, label: r.location! }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  const gradeOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated' && r.level)
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        if (!seen.has(r.level!)) { seen.add(r.level!); acc.push({ value: r.level!, label: r.level! }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  const teamOptions = useMemo(() => {
    if (!teamsEnabled) return [];
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated' && r.schedulerGroup)
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        if (!seen.has(r.schedulerGroup!)) { seen.add(r.schedulerGroup!); acc.push({ value: r.schedulerGroup!, label: r.schedulerGroup! }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources, teamsEnabled]);

  // ── Apply card filter
  const cardFilteredRows = useMemo(() => {
    if (activeCardFilter === 'demand-match') return data.rows.filter((r) => r.hasDemandMatch);
    return data.rows;
  }, [data.rows, activeCardFilter]);

  // ── Chart data
  const chartData = useMemo(() => {
    return [...cardFilteredRows]
      .sort((a, b) => {
        switch (chartSortBy) {
          case 'bench-desc': return b.benchWeeks - a.benchWeeks;
          case 'bench-asc':  return a.benchWeeks - b.benchWeeks;
          case 'name':       return a.name.localeCompare(b.name);
          default:           return b.benchWeeks - a.benchWeeks;
        }
      })
      .map((r) => ({
        name:           r.name.split(' ')[0],
        fullName:       r.name,
        benchWeeks:     r.benchWeeks,
        role:           r.role,
        lastProject:    r.lastProject,
        skills:         r.skills,
        bookingPct:     r.bookingPct,
        benchSinceDate: format(r.benchSinceDate, 'dd MMM yyyy'),
      }));
  }, [cardFilteredRows, chartSortBy]);

  // ── Chart sizing (horizontal bar)
  const X_AXIS_WIDTH  = 120;
  const BAR_HEIGHT    = 24;
  const chartInnerHeight = Math.max(200, chartData.length * (BAR_HEIGHT + 6) + 40);

  // ── Sort + paginate table
  const sortedRows = useMemo(() => {
    return [...cardFilteredRows].sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      switch (sortCol) {
        case 'name':        va = a.name;         vb = b.name;         break;
        case 'department':  va = a.department;   vb = b.department;   break;
        case 'location':    va = a.location;     vb = b.location;     break;
        case 'grade':       va = a.grade;        vb = b.grade;        break;
        case 'bench-since': va = a.benchSinceDate.getTime(); vb = b.benchSinceDate.getTime(); break;
        case 'bench-weeks': va = a.benchWeeks;   vb = b.benchWeeks;   break;
        case 'booking-pct': va = a.bookingPct;   vb = b.bookingPct;   break;
        default:            va = a.benchWeeks;   vb = b.benchWeeks;
      }
      if (typeof va === 'string') {
        return sortDir === 'asc' ? (va as string).localeCompare(vb as string) : (vb as string).localeCompare(va as string);
      }
      return sortDir === 'asc' ? (va as number) - (vb as number) : (vb as number) - (va as number);
    });
  }, [cardFilteredRows, sortCol, sortDir]);

  const totalPages    = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const paginatedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = useCallback((col: string) => {
    if (col === sortCol) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir(col === 'bench-weeks' ? 'desc' : 'asc');
    }
    setPage(1);
  }, [sortCol]);

  const handleReset = useCallback(() => {
    handlePeriodChange(defaultPeriod);
    setThreshold('u20');
    setBenchDuration('1w');
    setSelectedDepts([]);
    setSelectedRoles([]);
    setSelectedSkills([]);
    setSelectedLocations([]);
    setSelectedGrades([]);
    setSelectedTeams([]);
    setActiveCardFilter(null);
    setSortCol('bench-weeks');
    setSortDir('desc');
    setPage(1);
    setChartSortBy('bench-desc');
  }, [handlePeriodChange]);

  // ── Assign action — opens booking modal pre-populated
  const handleAssign = useCallback((row: BenchRow, e: React.MouseEvent) => {
    e.stopPropagation();
    const resource = resources.find((r) => r.id === row.id) ?? null;
    setBookingResource(resource);
    setBookingModalOpen(true);
  }, [resources]);

  // ── Export to Excel
  const handleExportExcel = useCallback(() => {
    if (!data.rows.length) {
      toast.error('Nothing to export', { description: 'No data matches the current filters.' });
      return;
    }
    const dateLabel = `${format(dateFrom, 'dd MMM yyyy')} – ${format(dateTo, 'dd MMM yyyy')}`;
    const rows = data.rows.map((r) => ({
      'Resource':           r.name,
      'Role':               r.role,
      'Department':         r.department,
      'Location':           r.location,
      'Grade':              r.grade,
      'Skills':             r.skills.join(', '),
      'Bench since':        format(r.benchSinceDate, 'dd MMM yyyy'),
      'Bench weeks':        r.benchWeeks,
      'Booking %':          `${r.bookingPct}%`,
      'Last project':       r.lastProject,
      'Next booking date':  r.nextBookingDate ? format(r.nextBookingDate, 'dd MMM yyyy') : 'None scheduled',
      'Period':             dateLabel,
    }));
    const wb  = XLSX.utils.book_new();
    const ws  = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0]).map((k) => ({
      wch: Math.max(k.length, ...rows.map((r) => String(r[k as keyof typeof r]).length)) + 2,
    }));
    XLSX.utils.book_append_sheet(wb, ws, 'Bench Report');
    const filename = `bench-report-${format(dateFrom, 'yyyy-MM-dd')}-to-${format(dateTo, 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success('Export complete', {
      description: `${data.rows.length} resource${data.rows.length !== 1 ? 's' : ''} exported to Excel.`,
    });
  }, [data.rows, dateFrom, dateTo]);

  // ── Active filter count for badge
  const filterCount =
    (selectedDepartments.length > 0 ? 1 : 0) +
    (selectedRoles.length > 0 ? 1 : 0) +
    (selectedSkills.length > 0 ? 1 : 0) +
    (selectedLocations.length > 0 ? 1 : 0) +
    (selectedGrades.length > 0 ? 1 : 0) +
    (selectedTeams.length > 0 ? 1 : 0) +
    (threshold !== 'u20' ? 1 : 0) +
    (benchDurationFilter !== '1w' ? 1 : 0);

  const hasSecondaryFilters = filterCount > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-6 py-4 shrink-0"
        style={{ borderBottom: '0.5px solid var(--color-border-tertiary, var(--border))' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <UserX className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground leading-tight">
              Bench report
            </h1>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Resources with no or low bookings — bench duration, cost and skills
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-4">
          <DataModeToggle screen="calendar" />
          <FeatureFeedbackCollector feature="Bench Report" />
          <HelpButton />
        </div>
      </div>

      {/* ── Filters bar ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 flex-wrap px-4 py-3 shrink-0"
        style={{
          background: 'var(--color-background-secondary, var(--muted))',
          borderBottom: '0.5px solid var(--color-border-tertiary, var(--border))',
        }}
      >
        {/* Period selector — stays outside filter drawer */}
        <Select value={period} onValueChange={(v) => handlePeriodChange(v as PeriodKey)}>
          <SelectTrigger className="h-8 text-[13px] w-auto min-w-[140px] shrink-0 bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="next2w">Next 2 weeks</SelectItem>
            <SelectItem value="next4w">Next 4 weeks</SelectItem>
            <SelectItem value="next8w">Next 8 weeks</SelectItem>
            <SelectItem value="next3m">Next 3 months</SelectItem>
          </SelectContent>
        </Select>

        {/* Prev / Next arrows — PRD §5.1 */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-background"
            onClick={() => {
              const diff = dateTo.getTime() - dateFrom.getTime();
              const newFrom = new Date(dateFrom.getTime() - diff);
              const newTo   = new Date(dateTo.getTime()   - diff);
              setDateFrom(newFrom);
              setDateTo(newTo);
              setPage(1);
              setLiveReportBookings([]);
              setHasFetchedBookings(false);
            }}
            aria-label="Previous period"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-background"
            onClick={() => {
              const diff = dateTo.getTime() - dateFrom.getTime();
              const newFrom = new Date(dateFrom.getTime() + diff);
              const newTo   = new Date(dateTo.getTime()   + diff);
              setDateFrom(newFrom);
              setDateTo(newTo);
              setPage(1);
              setLiveReportBookings([]);
              setHasFetchedBookings(false);
            }}
            aria-label="Next period"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Custom date inputs — PRD §5.1 */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Input
            type="date"
            className="h-8 text-[13px] w-[130px] bg-background"
            value={format(dateFrom, 'yyyy-MM-dd')}
            onChange={(e) => {
              const d = e.target.value ? startOfDay(parseISO(e.target.value)) : dateFrom;
              if (d < dateTo) { setDateFrom(d); setPage(1); setLiveReportBookings([]); setHasFetchedBookings(false); }
            }}
            aria-label="Period start date"
          />
          <span className="text-[12px] text-muted-foreground">to</span>
          <Input
            type="date"
            className="h-8 text-[13px] w-[130px] bg-background"
            value={format(dateTo, 'yyyy-MM-dd')}
            onChange={(e) => {
              const d = e.target.value ? startOfDay(parseISO(e.target.value)) : dateTo;
              if (d > dateFrom) { setDateTo(d); setPage(1); setLiveReportBookings([]); setHasFetchedBookings(false); }
            }}
            aria-label="Period end date"
          />
        </div>

        {/* Threshold segmented control — PRD §5.2 */}
        <div className="inline-flex h-8 rounded-md border border-input bg-background shrink-0">
          {([
            { key: 'u20'  as ThresholdFilter, label: '< 20%' },
            { key: 'u40'  as ThresholdFilter, label: '< 40%' },
            { key: 'u60'  as ThresholdFilter, label: '< 60%' },
            { key: 'u100' as ThresholdFilter, label: '< 100%' },
          ]).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => { setThreshold(key); setPage(1); }}
              className={[
                'px-2.5 text-[12px] font-medium transition-colors first:rounded-l-md last:rounded-r-md border-r last:border-r-0 border-input',
                threshold === key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {hasSecondaryFilters && (
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <X className="h-3 w-3" />
              Reset
            </button>
          )}
          {/* Filter button */}
          <Button
            variant="outline"
            size="icon"
            className="relative h-8 w-8 shrink-0 bg-background"
            onClick={handleOpenFilterDrawer}
            aria-label="Open filters"
          >
            <Filter className="h-4 w-4" />
            {filterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                {filterCount}
              </span>
            )}
          </Button>

          {/* Column config button */}
          <Button
            variant="outline"
            size="icon"
            className="relative h-8 w-8 shrink-0 bg-background"
            onClick={() => setColConfigOpen(true)}
            aria-label="Configure columns"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {visibleBenchCols.size < ALL_BENCH_COLS.length && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                {ALL_BENCH_COLS.length - visibleBenchCols.size}
              </span>
            )}
          </Button>

          {/* Export to Excel */}
          {canExport && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0 bg-background"
                  onClick={handleExportExcel}
                  aria-label="Export bench report to Excel"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export to Excel</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          )}
        </div>
      </div>

      {/* ── Column config sheet ─────────────────────────────────────────── */}
      <Sheet open={colConfigOpen} onOpenChange={setColConfigOpen}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Configure columns</SheetTitle>
          <SheetDescription className="sr-only">Toggle columns on or off for the bench report table.</SheetDescription>
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold leading-tight">Customise Columns</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Toggle columns on or off to personalise the table.</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">{visibleBenchCols.size}</div>
                <span className="text-sm text-muted-foreground">of {ALL_BENCH_COLS.length} columns showing</span>
              </div>
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={resetBenchCols}>
                <RotateCcw className="h-3.5 w-3.5" />Reset defaults
              </Button>
            </div>
          </SheetHeader>
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-2">
                {ALL_BENCH_COLS.map((col) => {
                  const isVisible = visibleBenchCols.has(col.key);
                  return (
                    <div key={col.key} className={['flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150', isVisible ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30' : 'bg-muted/10 border-border/30'].join(' ')}>
                      <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/30" />
                      <div className="flex-1 min-w-0">
                        <p className={['text-sm font-medium leading-snug', !isVisible ? 'text-muted-foreground' : 'text-foreground'].join(' ')}>{col.label}</p>
                        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">{col.description}</p>
                      </div>
                      <Switch checked={isVisible} onCheckedChange={() => toggleBenchCol(col.key)} aria-label={`${isVisible ? 'Hide' : 'Show'} ${col.label} column`} />
                    </div>
                  );
                })}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          <SheetFooter className="px-4 py-3 border-t shrink-0">
            <Button className="w-full" onClick={() => setColConfigOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Filter drawer ─────────────────────────────────────────────────── */}
      <Sheet open={filterDrawerOpen} onOpenChange={(open) => { if (!open) setFilterDrawerOpen(false); }}>
        <SheetContent side="right" className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Bench report filters</SheetTitle>
          <SheetDescription className="sr-only">Filter bench report by duration, department, role, skills, location and grade.</SheetDescription>
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Filters</span>
              <Badge variant="secondary" className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10">
                <Filter className="h-3 w-3" />
                {filterCount} {filterCount === 1 ? 'filter' : 'filters'} active
              </Badge>
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-4">
                {/* Bench threshold */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Booking threshold</label>
                  <Select value={draftThreshold} onValueChange={(v) => setDraftThreshold(v as ThresholdFilter)}>
                    <SelectTrigger className="h-8 text-[13px] bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="u20">Under 20% booked</SelectItem>
                      <SelectItem value="u40">Under 40% booked</SelectItem>
                      <SelectItem value="u60">Under 60% booked</SelectItem>
                      <SelectItem value="u100">Under 100% booked</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    Show resources with booking % below this threshold
                  </p>
                </div>

                <Separator />

                {/* Bench duration */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Bench duration (minimum)</label>
                  <Select value={draftBenchDuration} onValueChange={(v) => setDraftBenchDuration(v as BenchDurationFilter)}>
                    <SelectTrigger className="h-8 text-[13px] bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1w">1+ weeks</SelectItem>
                      <SelectItem value="2w">2+ weeks</SelectItem>
                      <SelectItem value="4w">4+ weeks</SelectItem>
                      <SelectItem value="8w">8+ weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                {/* Department */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Department</label>
                  <MultiSelect options={departmentOptions} placeholder="All departments" value={draftSelectedDepartments} onValueChange={setDraftSelectedDepts} maxDisplay={2} />
                </div>

                <Separator />

                {/* Role */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Role</label>
                  <MultiSelect options={roleOptions} placeholder="All roles" value={draftSelectedRoles} onValueChange={setDraftSelectedRoles} maxDisplay={2} />
                </div>

                <Separator />

                {/* Skills */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Skills</label>
                  <MultiSelect options={skillOptions} placeholder="All skills" value={draftSelectedSkills} onValueChange={setDraftSelectedSkills} maxDisplay={2} />
                </div>

                <Separator />

                {/* Location */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Location</label>
                  <MultiSelect options={locationOptions} placeholder="All locations" value={draftSelectedLocations} onValueChange={setDraftSelectedLocations} maxDisplay={2} />
                </div>

                {/* Grade */}
                {gradeOptions.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Grade</label>
                      <MultiSelect options={gradeOptions} placeholder="All grades" value={draftSelectedGrades} onValueChange={setDraftSelectedGrades} maxDisplay={2} />
                    </div>
                  </>
                )}

                {/* Teams — only when scheduler groups configured */}
                {teamsEnabled && (
                  <>
                    <Separator />
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Planner Group</label>
                      <MultiSelect options={teamOptions} placeholder="All planner groups" value={draftSelectedTeams} onValueChange={setDraftSelectedTeams} maxDisplay={2} />
                    </div>
                  </>
                )}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
              onClick={() => { handleReset(); setFilterDrawerOpen(false); }}
              disabled={!hasSecondaryFilters}
            >
              <X className="h-3.5 w-3.5" />
              Clear All
            </Button>
            <Button className="h-9 gap-1.5 px-5" onClick={handleApplyFilters}>
              <Check className="h-3.5 w-3.5" />
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Scrollable content area ───────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

        {isLiveDataLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading live bench data…</p>
          </div>
        ) : data.isEmpty ? (
          <EmptyState onReset={handleReset} onNavigateToUtilisation={onNavigateToUtilisation} />
        ) : (
          <>
            {/* ── Stat cards ────────────────────────────────────────────── */}
            <div className="flex gap-3 flex-wrap">
              {/* Card 1 — Resources on bench */}
              <StatCard
                value={String(data.summary.benchCount)}
                accent={
                  data.summary.benchCountAccent === 'destructive'
                    ? 'destructive'
                    : data.summary.benchCountAccent === 'warning'
                    ? 'warning'
                    : 'teal'
                }
                label="Resources on bench"
                subLabel="No or low bookings in period"
              />
              {/* Card 2 — Demand Match (clickable — PRD §5.5) */}
              <StatCard
                value={String(data.summary.demandMatchCount)}
                accent={data.summary.demandMatchCount > 0 ? 'teal' : 'default'}
                label="Demand match"
                subLabel="Bench resources with AI-matched open demand"
                isFilterable={data.summary.demandMatchCount > 0}
                isActive={activeCardFilter === 'demand-match'}
                onClick={() => {
                  setActiveCardFilter((prev) => prev === 'demand-match' ? null : 'demand-match');
                  setPage(1);
                }}
              />
              {/* Card 3 — Avg bench duration (PRD §5.5) */}
              <StatCard
                value={`${data.summary.avgBenchWeeks} ${data.summary.avgBenchWeeks === 1 ? 'week' : 'weeks'}`}
                accent={data.summary.avgBenchAccent}
                label="Avg bench duration"
                subLabel="Mean bench weeks across all bench resources"
              />
              {/* Card 4 — Estimated Cost (PRD §5.5) */}
              <StatCard
                value={`£${data.summary.estimatedWeeklyCost.toLocaleString()}`}
                accent={data.summary.estimatedWeeklyCost > 0 ? 'warning' : 'default'}
                label="Estimated weekly cost"
                subLabel="Bench headcount × avg daily rate × 5 days"
              />
            </div>

            {/* ── Threshold definition note ──────────────────────────────── */}
            <p className="text-[11px] text-muted-foreground -mt-2">
              Showing resources with less than{' '}
              <span className="font-medium">{thresholdToPct(threshold)}%</span>{' '}
              bookings in the selected period
            </p>

            {/* ── Active card filter pill ───────────────────────────────── */}
            {activeCardFilter && (
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-muted-foreground">Filtered by:</span>
                <button
                  type="button"
                  onClick={() => { setActiveCardFilter(null); setPage(1); }}
                  className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                >
                  In-demand skills on bench
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="shrink-0">
                    <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
                <span className="text-[11px] text-muted-foreground">
                  — showing {cardFilteredRows.length} of {data.rows.length} resources
                </span>
              </div>
            )}

            {/* ── Horizontal bar chart ──────────────────────────────────── */}
            <div
              className="rounded-[var(--radius-lg)] p-4 flex flex-col gap-3"
              style={{
                background: 'var(--background)',
                border: '0.5px solid var(--color-border-tertiary, var(--border))',
              }}
            >
              {/* Chart toolbar */}
              <div className="flex items-center justify-between gap-3 flex-wrap shrink-0">
                <span className="text-[12px] font-medium text-foreground">
                  {chartData.length} {chartData.length === 1 ? 'resource' : 'resources'} on bench
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">Sort by</span>
                  <Select
                    value={chartSortBy}
                    onValueChange={(v) => setChartSortBy(v as typeof chartSortBy)}
                  >
                    <SelectTrigger className="h-7 text-[12px] w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bench-desc">Bench duration (longest first)</SelectItem>
                      <SelectItem value="bench-asc">Bench duration (shortest first)</SelectItem>
                      <SelectItem value="name">Name (A–Z)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Horizontal bar chart — one row per benched resource */}
              <div className="relative overflow-hidden" ref={chartScrollRef}>
                <div className="overflow-y-auto overflow-x-hidden" style={{ maxHeight: 400 }}>
                  <div style={{ width: '100%', height: chartInnerHeight }}>
                    <ResponsiveContainer width="100%" height={chartInnerHeight}>
                      <BarChart
                        layout="vertical"
                        data={chartData}
                        margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                        barSize={BAR_HEIGHT}
                      >
                        <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis
                          type="number"
                          domain={[0, period === 'next3m' ? 15 : 12]}
                          ticks={period === 'next3m' ? [0, 3, 6, 9, 12, 15] : [0, 3, 6, 9, 12]}
                          tickFormatter={(v: number) => `${v}w`}
                          tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={X_AXIS_WIDTH}
                          tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(n: string) => n.length > 14 ? `${n.slice(0, 13)}…` : n}
                        />
                        <RechartsTooltip
                          content={<BenchChartTooltip />}
                          cursor={{ fill: 'var(--accent)', opacity: 0.3 }}
                        />
                        <Bar dataKey="benchWeeks" name="benchWeeks" radius={[0, 4, 4, 0]}>
                          {chartData.map((entry, idx) => (
                            <Cell
                              key={idx}
                              fill={benchBarColour(entry.benchWeeks)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-4 px-1">
                {[
                  { colour: AMBER(),      label: 'Under 2 weeks' },
                  { colour: DARK_AMBER(), label: '2–4 weeks' },
                  { colour: RED(),        label: 'Over 4 weeks' },
                ].map(({ colour, label }) => (
                  <span key={label} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="inline-block h-3 w-3 rounded-[2px] shrink-0" style={{ background: colour }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* ── Data table ────────────────────────────────────────────── */}
            <div
              className="rounded-[var(--radius-lg)]"
              style={{
                border: '0.5px solid var(--color-border-tertiary, var(--border))',
              }}
            >
              <div className="overflow-x-auto rounded-[var(--radius-lg)]">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr style={{ background: 'var(--color-background-secondary, var(--muted))' }}>
                      <SortHeader label="Name / Role"    col="name"        sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={220} />
                      {visibleBenchCols.has('department') && <SortHeader label="Department"    col="department"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                      {visibleBenchCols.has('skills') && <th className="px-3 py-2.5 font-medium text-[12px] text-muted-foreground text-left whitespace-nowrap">Skills</th>}
                      {visibleBenchCols.has('grade') && <SortHeader label="Grade" col="grade" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                      {visibleBenchCols.has('location') && <SortHeader label="Location"      col="location"    sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                      {visibleBenchCols.has('benchSince') && <SortHeader label="Bench Since"   col="bench-since" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                      {visibleBenchCols.has('benchDuration') && <SortHeader label="Bench Duration" col="bench-weeks" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                      {visibleBenchCols.has('lastProject') && <th className="px-3 py-2.5 font-medium text-[12px] text-muted-foreground text-left whitespace-nowrap">Last Project</th>}
                      {visibleBenchCols.has('bookingPct') && <SortHeader label="Booking %" col="booking-pct" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleBenchCols.has('weekSlots') && <th className="px-3 py-2.5 font-medium text-[12px] text-muted-foreground text-left whitespace-nowrap">Week Slots</th>}
                      {visibleBenchCols.has('action') && <th className="px-3 py-2.5 font-medium text-[12px] text-muted-foreground text-left whitespace-nowrap">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, rowIdx) => (
                      <tr
                        key={row.id}
                        onClick={() => setDrawerRow(row)}
                        className="cursor-pointer transition-colors"
                        style={{
                          height: 48,
                          background: rowIdx % 2 === 0 ? 'var(--background)' : 'var(--color-background-secondary, var(--muted))',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#E1F5EE'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = rowIdx % 2 === 0 ? 'var(--background)' : 'var(--color-background-secondary, var(--muted))'; }}
                      >
                        {/* Name / Role with demand match Sparkles indicator (PRD §5.7) */}
                        <td className="px-3 py-2" style={{ minWidth: 220 }}>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Avatar className="h-7 w-7 shrink-0">
                              <AvatarImage src={row.photoUrl} alt={row.name} />
                              <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                                {row.initials}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-[13px] font-medium text-foreground truncate leading-snug">{row.name}</p>
                                {row.hasDemandMatch && <DemandMatchBadge />}
                              </div>
                              <p className="text-[11px] text-muted-foreground truncate leading-snug">{row.role}</p>
                            </div>
                          </div>
                        </td>
                        {/* Department */}
                        {visibleBenchCols.has('department') && <td className="px-3 py-2 text-[13px] text-foreground whitespace-nowrap">{row.department}</td>}
                        {/* Skills */}
                        {visibleBenchCols.has('skills') && <td className="px-3 py-2"><SkillTags skills={row.skills} /></td>}
                        {/* Grade */}
                        {visibleBenchCols.has('grade') && <td className="px-3 py-2 text-[13px] text-foreground whitespace-nowrap">{row.grade}</td>}
                        {/* Location */}
                        {visibleBenchCols.has('location') && <td className="px-3 py-2 text-[13px] text-foreground whitespace-nowrap">{row.location}</td>}
                        {/* Bench since */}
                        {visibleBenchCols.has('benchSince') && <td className="px-3 py-2 whitespace-nowrap"><BenchSinceCell date={row.benchSinceDate} weeks={row.benchWeeks} /></td>}
                        {/* Bench duration */}
                        {visibleBenchCols.has('benchDuration') && <td className="px-3 py-2 whitespace-nowrap"><BenchWeeksCell weeks={row.benchWeeks} /></td>}
                        {/* Last project */}
                        {visibleBenchCols.has('lastProject') && (
                          <td className="px-3 py-2 text-[13px] whitespace-nowrap max-w-[160px]">
                            {row.lastProject === '—' ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <span className="text-foreground truncate block">{row.lastProject}</span>
                            )}
                          </td>
                        )}
                        {/* Booking % */}
                        {visibleBenchCols.has('bookingPct') && <td className="px-3 py-2 whitespace-nowrap"><BookingPctCell pct={row.bookingPct} /></td>}
                        {/* Week slots — mini per-week booking % grid (PRD §5.7) */}
                        {visibleBenchCols.has('weekSlots') && (
                          <td className="px-3 py-2">
                            <WeekSlotsGrid slots={row.weekSlots} />
                          </td>
                        )}
                        {/* Action */}
                        {visibleBenchCols.has('action') && (
                          <td className="px-3 py-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-[12px] gap-1 border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-950 whitespace-nowrap"
                              onClick={(e) => handleAssign(row, e)}
                            >
                              Assign ›
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>

                  {/* Footer totals row */}
                  <tfoot>
                    <tr
                      style={{
                        background: 'var(--color-background-secondary, var(--muted))',
                        borderTop: '0.5px solid var(--color-border-primary, var(--border))',
                      }}
                    >
                      <td
                        className="px-3 py-2.5 font-medium text-[13px] text-foreground"
                        colSpan={2}
                      >
                        <span className="font-medium text-foreground">{sortedRows.length}</span>{' '}
                        {sortedRows.length === 1 ? 'resource' : 'resources'} on bench
                        {data.summary.totalBenchDays > 0 && (
                          <> · <span className="font-medium">{data.summary.totalBenchDays}</span> total bench days</>
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* ── Pagination ────────────────────────────────────────────── */}
            <div className="flex items-center justify-between shrink-0 pb-2">
              <p className="text-[13px] text-muted-foreground">
                Showing {Math.min((page - 1) * pageSize + 1, sortedRows.length)}–
                {Math.min(page * pageSize, sortedRows.length)} of {sortedRows.length} resources
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 text-[13px]"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </Button>
                <span className="text-[13px] text-muted-foreground px-1">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-8 text-[13px]"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[13px] text-muted-foreground">Rows per page:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}
                >
                  <SelectTrigger className="h-8 w-20 text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="99999">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

          </>
        )}
      </div>

      {/* ── Resource detail side drawer ───────────────────────────────────── */}
      <BenchDetailDrawer
        row={drawerRow}
        open={drawerRow !== null}
        onClose={() => setDrawerRow(null)}
        onAssign={handleAssign}
      />

      {/* ── Add Booking modal — pre-populated with selected resource ──────── */}
      <ComprehensiveBookingModal
        open={bookingModalOpen}
        onOpenChange={setBookingModalOpen}
        onSave={async (booking) => {
          await contextAddBooking(booking);
          setBookingModalOpen(false);
          toast.success('Booking saved', {
            description: `${bookingResource?.name ?? 'Resource'} has been assigned.`,
          });
          if (currentMode === 'live') {
            fetchLiveBookings(dateFrom, dateTo);
          }
        }}
        onUpdate={async (id, booking) => { await contextUpdateBooking(id as string, booking); }}
        onDelete={(booking) => { contextDeleteBooking(booking.id); setBookingModalOpen(false); }}
        resources={resources}
        projectDetails={projectDetails}
        existingBookings={bookings}
        preselectedResource={bookingResource ?? undefined}
        mode="create"
      />
    </div>
  );
}
